import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputMask } from 'primereact/inputmask';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { TreeNode } from 'primereact/treenode';
import { SubmitHandler, useForm } from 'react-hook-form';

import { ScheduleTemplate } from 'constants/ScheduleTemplate';

import { ScheduleService } from '../components/services/ScheduleService';
import { ScheduleSchema } from '../constants/schemas';
import { handleApiErrors } from '../helpers';
import { useApiCall } from '../hooks/useApiCall';
import { ScheduleWithSettings } from '../types/interfaces';
import { ObjectForSelection } from '../types/interfaces/ObjectForSelection';
import { ScheduleProps } from '../types/props';
import { generateGUID } from '../utils';

import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';

import '../ui/css/ScheduleCard.css';

interface RefInterface {
  handleCancel: () => void;
}

const ScheduleCard = React.forwardRef<RefInterface, ScheduleProps>(
  (
    { scheduleProps, onAdd, onUpdate, onDelete, closeDialog }: ScheduleProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      setError,
      formState: { isDirty, errors }
    } = useForm({
      resolver: zodResolver(ScheduleSchema),
      defaultValues: scheduleProps
    });

    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [schedulesList, setSchedulesList] = useState<TreeNode[]>([]);
    const [selectedTemplate, setSelectedTemplate] =
      useState<ObjectForSelection | null>(null);
    const [showTemplateWarning, setShowTemplateWarning] = useState(false);
    const toast = useRef<Toast>(null);
    const scheduleName = watch('name');
    const scheduleId = watch('id');
    const scheduleSettings = watch('scheduleSettings');

    const periodTypes = [
      { label: 'Дни', value: 1 },
      { label: 'Недели', value: 2 },
      { label: 'Месяцы', value: 3 }
    ];

    const loadScheduleData = useCallback(async () => {
      const mergeWithDefaults = (
        schedule: Partial<ScheduleWithSettings>
      ): ScheduleWithSettings => ({
        ...ScheduleTemplate,
        ...schedule,
        scheduleSettings: schedule.scheduleSettings
          ? schedule.scheduleSettings.map((setting) => ({
              ...setting,
              guid: setting.guid || generateGUID()
            }))
          : ScheduleTemplate.scheduleSettings
      });

      if (scheduleProps.id && scheduleProps.id !== 0) {
        try {
          const response = await ScheduleService.getScheduleById(
            Number(scheduleProps.id)
          );

          if (response.data && response.data) {
            reset(mergeWithDefaults(response.data));
          } else {
            reset(ScheduleTemplate);
            console.error('Error loading schedule:', response);
          }
        } catch (error) {
          console.error('Failed to load schedule:', error);
          reset(ScheduleTemplate);
        }
      } else {
        reset(ScheduleTemplate);
      }
    }, [scheduleProps.id, reset]);

    useApiCall(loadScheduleData);

    const onSubmit: SubmitHandler<ScheduleWithSettings> = useCallback(
      async (data) => {
        const newData = { ...data };
        const dataId = data.id;
        let result;

        if (onUpdate && scheduleId) {
          result = await onUpdate(Number(dataId), newData);
        }
        if (onAdd && !scheduleId) {
          newData.id = 0;
          result = await onAdd(newData);
        }
        if (result?.data.isSuccess) {
          closeDialog?.();
        } else {
          handleApiErrors({
            error: result?.data.error,
            setError,
            fieldMapping: {
              SameName: {
                field: 'name',
                message:
                  'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название расписания'
              }
            }
          });

          if (!result?.data.error) {
            toast.current?.show({
              severity: 'error',
              summary: 'Ошибка сохранения',
              detail: 'Не удалось сохранить расписание'
            });
          }
        }
      },
      [closeDialog, onAdd, onUpdate, setError]
    );

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      }
      if (closeDialog && !isDirty) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = async () => {
      if (onDelete) {
        await onDelete(scheduleId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    useEffect(() => {
      const loadSchedules = async () => {
        const result = await ScheduleService.getSchedulesForSelection();
        if (result) {
          const filtredList = result.data.value.filter(
            (item) => item.id !== scheduleId
          );
          setSchedulesList(filtredList);
        }
      };
      loadSchedules();
    }, []);

    // Обработчик выбора шаблона
    const handleTemplateSelect = (template: ObjectForSelection) => {
      setSelectedTemplate(template);
      if (isDirty || scheduleId !== 0) {
        setShowTemplateWarning(true);
        return;
      } else {
        handleTemplateSelectionConfirmation(template);
      }
    };

    const handleTemplateSelectionConfirmation = async (
      template?: ObjectForSelection
    ) => {
      const targetTemplate = template?.id ?? selectedTemplate?.id;
      if (targetTemplate) {
        try {
          const response =
            await ScheduleService.getScheduleById(targetTemplate);
          if (response.data) {
            const preparedData = response.data.scheduleSettings.map(
              (setting) => ({
                ...setting,
                id: 0,
                guid: Math.random().toString(36).substr(2, 9)
              })
            );

            setValue('scheduleSettings', preparedData, { shouldDirty: true });
            setValue('periodType', response.data.periodType, {
              shouldDirty: true
            });
            setValue('periodValue', response.data.periodValue, {
              shouldDirty: true
            });
          }
        } catch (error) {
          toast.current?.show({
            severity: 'error',
            summary: 'Ошибка',
            detail: 'Не удалось загрузить шаблон'
          });
        }
      }
      setSelectedTemplate(null);
      setShowTemplateWarning(false);
    };

    const handleTempleteSelectionCancel = () => {
      setSelectedTemplate(null);
      setShowTemplateWarning(false);
    };

    const addScheduleSetting = () => {
      const newSettings = [...scheduleSettings];
      newSettings.push({
        id: 0,
        dayNumber: 1,
        weekNumber: 1,
        workdayStart: '',
        workdayDuration: '',
        breakDuration: '',
        guid: generateGUID()
      });
      setValue('scheduleSettings', newSettings, { shouldDirty: true });
    };

    const removeScheduleSetting = (guid: string) => {
      const newSettings = scheduleSettings.filter(
        (setting) => setting.guid !== guid
      );
      setValue('scheduleSettings', newSettings, { shouldDirty: true });
    };

    return (
      <div className="dialog-tabless-card operationTypes-scores-card operationTypes-card__roles">
        <Toast ref={toast} />
        <div className=" gap-3 pb-0">
          <Divider />
          <div className="operationType-card__column">
            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center w-full">
                  <label htmlFor="value" className="schedule-card__label p-0">
                    Название<span className="required">*</span>:
                  </label>
                  <div className="flex schedule-card__input w-full gap-4">
                    <div className="p-0" style={{ width: '45%' }}>
                      <InputText className="w-full" {...register('name')} />
                    </div>
                    <div className="p-0" style={{ width: '55%' }}>
                      <Dropdown
                        value={selectedTemplate}
                        options={schedulesList}
                        onChange={(e) => handleTemplateSelect(e.value)}
                        optionLabel="label"
                        placeholder="Использовать другое расписание как шаблон"
                        filter
                        showClear
                        style={{ width: '100%' }}
                        scrollHeight="200px"
                      />
                    </div>
                  </div>
                </div>
                <p
                  className={`operationTypes-scores-card-errors card-errors ${
                    errors.name ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.name ? errors.name.message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center w-full">
                  <label
                    htmlFor="description"
                    className="schedule-card__label p-0"
                  >
                    Описание:
                  </label>
                  <div className="schedule-card__input w-full p-0">
                    <InputText
                      className="w-full"
                      {...register('description')}
                    />
                  </div>
                </div>
                <p
                  className={`operationTypes-scores-card-errors card-errors ${
                    errors.description ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.description ? errors.description.message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center w-full">
                  <label
                    htmlFor="periodValue"
                    className="schedule-card__label p-0"
                  >
                    Повторять каждые<span className="required">*</span>:
                  </label>
                  <div className="flex gap-2 schedule-card__input w-full">
                    <div>
                      <InputNumber
                        value={watch('periodValue')}
                        onValueChange={(e) =>
                          setValue('periodValue', e.value || 0, {
                            shouldDirty: true,
                            shouldValidate: true
                          })
                        }
                        min={0}
                        showButtons
                      />
                      <p
                        className={`operationTypes-scores-card-errors card-errors ${
                          errors.periodValue
                            ? 'text-red-500'
                            : 'text-transparent'
                        } `}
                      >
                        {errors.periodValue ? errors.periodValue.message : ' '}
                      </p>
                    </div>
                    <div style={{ width: '' }}>
                      <Dropdown
                        value={watch('periodType')}
                        options={periodTypes}
                        onChange={(e) =>
                          setValue('periodType', e.value, {
                            shouldDirty: true,
                            shouldValidate: true
                          })
                        }
                        optionLabel="label"
                        optionValue="value"
                        style={{ width: '100%' }}
                        placeholder="период повторения"
                      />
                      <p
                        className={`operationTypes-scores-card-errors card-errors ${
                          errors.periodType
                            ? 'text-red-500'
                            : 'text-transparent'
                        } `}
                      >
                        {errors.periodType ? errors.periodType.message : ' '}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Tooltip
              target=".p-inputgroup-addon"
              mouseTrack
              mouseTrackLeft={10}
            />

            <div className="schedule-days-container">
              <div
                className="schedule-day-header"
                style={{
                  marginRight: scheduleSettings.length > 7 ? '18px' : ''
                }}
              >
                <div className="schedule-day-column">Номер дня</div>
                <div className="schedule-day-column">Номер недели</div>
                <div className="schedule-day-column">Начало (ЧЧ:ММ)</div>
                <div className="schedule-day-column">
                  Продолжительность работы (ЧЧ:ММ)
                </div>
                <div className="schedule-day-column">Перерыв (ЧЧ:ММ)</div>
                <div className="schedule-day-column-actions"></div>
              </div>
              <div
                className="scrollable-container"
                id="tags-scroll"
                style={{
                  width: '100%',
                  height: 'fit-content',
                  maxHeight: '350px',
                  overflow: 'auto'
                }}
              >
                <div
                  className="flex flex-column scrollable-content"
                  style={{ width: '99%' }}
                >
                  {scheduleSettings.map((setting, index) => (
                    <div
                      key={setting.guid}
                      className="schedule-day-row p-fluid"
                    >
                      <div
                        className={`p-inputgroup ${
                          errors?.scheduleSettings?.[index]?.dayNumber
                            ? 'schedule-dayItem-error'
                            : ''
                        }`}
                      >
                        <span
                          className="p-inputgroup-addon"
                          data-pr-tooltip={
                            errors?.scheduleSettings?.[index]?.dayNumber
                              ? errors?.scheduleSettings?.[index]?.dayNumber
                                  .message
                              : ''
                          }
                          data-pr-position="right"
                        >
                          <i className="pi pi-calendar"></i>
                        </span>
                        <InputNumber
                          value={setting.dayNumber}
                          onValueChange={(e) =>
                            setValue(
                              `scheduleSettings.${index}.dayNumber`,
                              e.value ?? 0,
                              { shouldDirty: true, shouldValidate: true }
                            )
                          }
                          min={1}
                          max={7}
                          showButtons
                          className="compact-input"
                        />
                      </div>

                      <div
                        className={`p-inputgroup ${
                          errors?.scheduleSettings?.[index]?.weekNumber
                            ? 'schedule-dayItem-error'
                            : ''
                        }`}
                      >
                        <span
                          className="p-inputgroup-addon"
                          data-pr-tooltip={
                            errors?.scheduleSettings?.[index]?.weekNumber
                              ? errors?.scheduleSettings?.[index]?.weekNumber
                                  .message
                              : ''
                          }
                          data-pr-position="right"
                        >
                          <i className="pi pi-calendar-plus"></i>
                        </span>
                        <InputNumber
                          value={setting.weekNumber}
                          onValueChange={(e) =>
                            setValue(
                              `scheduleSettings.${index}.weekNumber`,
                              e.value ?? 0,
                              { shouldDirty: true, shouldValidate: true }
                            )
                          }
                          min={1}
                          max={52}
                          showButtons
                          className="compact-input"
                        />
                      </div>

                      <div
                        className={`p-inputgroup ${
                          errors?.scheduleSettings?.[index]?.workdayStart
                            ? 'schedule-dayItem-error'
                            : ''
                        }`}
                      >
                        <span
                          className="p-inputgroup-addon"
                          data-pr-tooltip={
                            errors?.scheduleSettings?.[index]?.workdayStart
                              ? errors?.scheduleSettings?.[index]?.workdayStart
                                  .message
                              : ''
                          }
                          data-pr-position="right"
                        >
                          <i className="pi pi-clock"></i>
                        </span>
                        <InputMask
                          mask="99:99"
                          value={setting.workdayStart}
                          onChange={(e) =>
                            setValue(
                              `scheduleSettings.${index}.workdayStart`,
                              e.value || '',
                              { shouldDirty: true, shouldValidate: true }
                            )
                          }
                          placeholder="ЧЧ:ММ"
                          slotChar="ЧЧ:ММ"
                          className="compact-input"
                        />
                      </div>

                      <div
                        className={`p-inputgroup ${
                          errors?.scheduleSettings?.[index]?.workdayDuration
                            ? 'schedule-dayItem-error'
                            : ''
                        }`}
                      >
                        <span
                          className="p-inputgroup-addon"
                          data-pr-position="right"
                          data-pr-tooltip={
                            errors?.scheduleSettings?.[index]?.workdayDuration
                              ? errors?.scheduleSettings?.[index]
                                  ?.workdayDuration.message
                              : ''
                          }
                        >
                          <i className="pi pi-hourglass"></i>
                        </span>
                        <InputMask
                          mask="99:99"
                          value={setting.workdayDuration}
                          onChange={(e) =>
                            setValue(
                              `scheduleSettings.${index}.workdayDuration`,
                              e.value || '',
                              { shouldDirty: true, shouldValidate: true }
                            )
                          }
                          placeholder="ЧЧ:ММ"
                          slotChar="ЧЧ:ММ"
                          className="compact-input"
                        />
                      </div>

                      <div
                        className={`p-inputgroup ${
                          errors?.scheduleSettings?.[index]?.breakDuration
                            ? 'schedule-dayItem-error'
                            : ''
                        }`}
                      >
                        <span
                          className="p-inputgroup-addon"
                          data-pr-position="right"
                          data-pr-tooltip={
                            errors?.scheduleSettings?.[index]?.breakDuration
                              ? errors?.scheduleSettings?.[index]?.breakDuration
                                  .message
                              : ''
                          }
                        >
                          <i className="pi pi-pause"></i>
                        </span>
                        <InputMask
                          mask="99:99"
                          value={setting.breakDuration?.toString() || ''}
                          onChange={(e) =>
                            setValue(
                              `scheduleSettings.${index}.breakDuration`,
                              e.value || '',
                              { shouldDirty: true, shouldValidate: true }
                            )
                          }
                          placeholder="ЧЧ:ММ"
                          slotChar="ЧЧ:ММ"
                          className="compact-input"
                        />
                      </div>

                      <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger p-button-text"
                        onClick={() => removeScheduleSetting(setting.guid)}
                        tooltip="Удалить день"
                        tooltipOptions={{ position: 'top' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <Button
                icon="pi pi-plus"
                label="Добавить день"
                className="p-button-outlined mt-2"
                onClick={addScheduleSetting}
              />
            </div>
          </div>
        </div>

        <div className="operationType-card__buttons default-card__buttons">
          <div className="flex gap-3">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              onClick={handleDelete}
              className="p-button-danger"
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={() => setShowDialog(false)}
          onContinue={() => {
            setShowDialog(false);
            closeDialog?.();
          }}
        />

        <ConfirmationDialog
          visible={showTemplateWarning}
          onCancel={handleTempleteSelectionCancel}
          onContinue={handleTemplateSelectionConfirmation}
          header="Предупреждение"
          message="Применение шаблона перезапишет текущие настройки расписания. Продолжить?"
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="вид работ"
          entityName={scheduleName}
        />
      </div>
    );
  }
);

ScheduleCard.displayName = 'ScheduleCard';

export default ScheduleCard;
